/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import Api from 'services/api';
import { AudioWaveRecorder } from 'components/atoms/audio_wave';
import { useAudioRecorderController } from 'hooks/useAudioRecorderController';
import { speechToText } from 'services/actions/chatbot_actions';
import createTrack from 'services/actions/tracker_actions';
import { captalizeFirstLetter } from 'services/captalize';
import iconIaGradiente from 'assets/images/icone-ia-gradiente.svg';
import MatchPerfil from 'components/molecules/match_perfil';
import { Overlay, Panel } from './styles';

interface MatchPersonalizarModalProps {
  bulletinFilterId?: number | string;
}

interface HistoricoItem {
  complemento: string;
  contato?: string | null;
  created_at?: string;
  updated_at?: string;
}

const PLACEHOLDER =
  'Exemplo: Procuro licitações de material hospitalar no Sudeste, com exigência de ANVISA e valor acima de R$ 50 mil.';

/** Rótulo do grupo do histórico: "Hoje", "Ontem", dia da semana ou a data cheia. */
const dayLabel = (date: moment.Moment) => {
  const today = moment().startOf('day');
  const diff = today.diff(date.clone().startOf('day'), 'days');

  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  if (diff < 7) return captalizeFirstLetter(date.locale('pt-br').format('dddd'));
  return date.format('DD/MM/YYYY');
};

/** Abre via evento global 'open-match-personalizar' (disparado pelos botões "Personalizar"). */
const MatchPersonalizarModal: React.FC<MatchPersonalizarModalProps> = ({ bulletinFilterId }) => {
  const [open, setOpen] = useState(false);
  const [texto, setTexto] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [aba, setAba] = useState<'personalizar' | 'perfil'>('personalizar');
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [transcribing, setTranscribing] = useState(false);
  const { waveRef, isRecording, startRecording, cancelRecording, stopRecording } =
    useAudioRecorderController();

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('open-match-personalizar', openHandler);
    return () => window.removeEventListener('open-match-personalizar', openHandler);
  }, []);

  useEffect(() => {
    if (!open || loaded) return;
    Api.get('boletim_web/public/match/personalizacao')
      .then((res) => {
        setTexto(res.data?.complemento || '');
        setHistorico(res.data?.historico || []);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  /** Mesmo fluxo do campo de anotações: grava o áudio e transcreve no backend. */
  const handleFinishRecording = useCallback(async (blob: Blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'audio.webm');

    try {
      setTranscribing(true);
      createTrack('Match - Ditar personalização');

      const response = await speechToText(formData);
      const transcrito = response?.data?.text;

      if (response.status === 200 && transcrito) {
        setTexto((prev) => (prev ? `${prev} ${transcrito}` : transcrito).trim());
      }
    } catch (error) {
      console.error('Erro na transcrição:', error);
    } finally {
      setTranscribing(false);
    }
  }, []);

  const salvar = useCallback(() => {
    setSaving(true);
    setSaved(false);
    Api.post('boletim_web/public/match/personalizacao', {
      complemento: texto,
      bulletin_filter_id: bulletinFilterId,
    })
      .then((res) => {
        setSaved(true);
        setHistorico(res.data?.historico || historico);
      })
      .catch((e) => {
        // eslint-disable-next-line no-alert
        alert(
          e?.response?.status === 422
            ? 'Não conseguimos montar seu perfil com esse texto. Descreva os produtos ou serviços que sua empresa fornece.'
            : 'Não foi possível salvar. Tente novamente.',
        );
      })
      .finally(() => setSaving(false));
  }, [texto, bulletinFilterId, historico]);

  /** Histórico ordenado do mais recente para o mais antigo e agrupado por dia. */
  const historyGroups = useMemo(() => {
    const parsed = historico
      .map((item) => ({ ...item, date: moment(item.updated_at || item.created_at) }))
      .filter((item) => item.date.isValid())
      .sort((a, b) => b.date.valueOf() - a.date.valueOf());

    return parsed.reduce<Array<{ label: string; items: typeof parsed }>>((groups, item) => {
      const label = dayLabel(item.date);
      const last = groups[groups.length - 1];

      if (last && last.label === label) last.items.push(item);
      else groups.push({ label, items: [item] });

      return groups;
    }, []);
  }, [historico]);

  const closeModal = () => {
    // sem isso o modal reabre já "gravando", com o microfone preso do estado anterior
    cancelRecording();
    setOpen(false);
    setSaved(false);
    setShowHistory(false);
    setAba('personalizar');
  };

  if (!open) return null;

  return (
    <Overlay onClick={closeModal}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <header>
          <span className="title">
            <img src={iconIaGradiente} alt="IA" width={26} height={24} />
            Personalizar match de licitações
          </span>
          <button type="button" className="close" onClick={closeModal} aria-label="Fechar">
            <span className="material-icons">close</span>
          </button>
        </header>

        <nav className="abas">
          <button
            type="button"
            className={aba === 'personalizar' ? 'aba aba--ativa' : 'aba'}
            onClick={() => setAba('personalizar')}
          >
            Personalizar
          </button>
          <button
            type="button"
            className={aba === 'perfil' ? 'aba aba--ativa' : 'aba'}
            onClick={() => setAba('perfil')}
          >
            Meu perfil
          </button>
        </nav>

        {aba === 'perfil' ? (
          <div className="content">
            <MatchPerfil />
          </div>
        ) : (
          <div className="content">
            <div>
              <p className="field-title">Descreva o edital perfeito para sua empresa</p>
              <p className="field-hint">
                Você pode informar itens, região, exigências, documentos ou características
                importantes.
              </p>

              <div className="dictate-row">
                {isRecording && (
                  <div className="dictate-wave">
                    <AudioWaveRecorder ref={waveRef} variant="white" />
                  </div>
                )}

                {isRecording || transcribing ? (
                  <div className="dictate-controls">
                    <button
                      type="button"
                      className="dictate-cancel"
                      onClick={cancelRecording}
                      disabled={transcribing}
                      aria-label="Cancelar ditado"
                    >
                      <span className="material-icons">close</span>
                    </button>
                    <button
                      type="button"
                      className="dictate-confirm"
                      onClick={() => stopRecording(handleFinishRecording)}
                      disabled={transcribing}
                      aria-label="Concluir ditado"
                    >
                      {transcribing ? (
                        <span className="dictate-transcribing">Transcrevendo…</span>
                      ) : (
                        <span className="material-icons">check</span>
                      )}
                    </button>
                  </div>
                ) : (
                  <button type="button" className="dictate" onClick={startRecording}>
                    <span className="material-icons">mic</span>
                    Ditar texto
                  </button>
                )}
              </div>

              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={loaded ? PLACEHOLDER : 'Carregando…'}
                disabled={!loaded}
              />
            </div>

            {saved && (
              <div className="alert-saved">
                Personalização salva com sucesso. As recomendações podem levar um tempo para
                refletir as novas preferências.
              </div>
            )}

            <div className="actions">
              <button
                type="button"
                className="history-toggle"
                onClick={() => setShowHistory((prev) => !prev)}
              >
                Histórico de edição
                <span className="material-icons">
                  {showHistory ? 'expand_less' : 'expand_more'}
                </span>
              </button>

              <button type="button" className="save" onClick={salvar} disabled={saving || !loaded}>
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>

            {showHistory && (
              <div className="history">
                {historyGroups.length === 0 && (
                  <p className="history-empty">Nenhuma edição anterior.</p>
                )}
                {historyGroups.map((group, groupIndex) => (
                  <React.Fragment key={group.label}>
                    <span className="history-day">{group.label}</span>
                    {group.items.map((item, itemIndex) => {
                      const isCurrent = groupIndex === 0 && itemIndex === 0;

                      return (
                        <div
                          className={`history-item ${isCurrent ? 'current' : ''}`}
                          key={item.date.valueOf()}
                        >
                          <div className="history-item-header">
                            <b>
                              {item.date.format('DD/MM/YYYY [às] HH:mm')}
                              {item.contato ? ` · ${item.contato}` : ''}
                            </b>
                            {isCurrent && <span>Versão atual</span>}
                          </div>
                          <p className="history-item-text">{item.complemento}</p>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </Panel>
    </Overlay>
  );
};

export default MatchPersonalizarModal;
